import { useState } from 'react';

/** Two display modes:
 *   - 'auto' (default): relative for recent ("3m ago"), date for older —
 *     used for notes/links lists where each item stands on its own.
 *   - 'time': time-of-day only ("7:53 PM") — used inside chat where the
 *     day context comes from the surrounding day-divider header. */
type Mode = 'auto' | 'time';

/** Click toggles to absolute timestamp; hover shows absolute via tooltip.
 *  Renders inside <time datetime="ISO"> for screen readers + SEO crawlers. */
export function RelativeTime({
  ts,
  mode = 'auto',
  className,
}: {
  ts: number;
  mode?: Mode;
  className?: string;
}) {
  const [showAbs, setShowAbs] = useState(false);
  const date = new Date(ts);
  const abs = date.toLocaleString();
  const label = showAbs ? abs : mode === 'time' ? timeOfDay(date) : timeShort(ts);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setShowAbs((v) => !v);
      }}
      title={abs}
      className={
        className ??
        'cursor-pointer rounded font-mono text-[10px] tabular-nums text-muted-foreground/60 transition-colors hover:text-foreground'
      }
      aria-label={`Posted ${abs}`}
    >
      <time dateTime={date.toISOString()}>{label}</time>
    </button>
  );
}

/** Time-of-day only — "7:53 PM". Used inside chat where the day is in
 *  the divider above. Locale-aware. */
export function timeOfDay(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/** Day-divider label for chat / notes lists. Slack/iMessage rules:
 *   - Today
 *   - Yesterday
 *   - Within last 6 days: weekday ("Monday")
 *   - Same year: "May 21"
 *   - Older: "May 21, 2025" */
export function dayDividerLabel(ts: number): string {
  const d = new Date(ts);
  const now = new Date();
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dDay = start(d);
  const nDay = start(now);
  const diffDays = Math.round((nDay - dDay) / (24 * 60 * 60 * 1000));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays > 1 && diffDays < 7) {
    return d.toLocaleDateString(undefined, { weekday: 'long' });
  }
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
  }
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

/** Calendar-day key — "2026-05-23". Use to group consecutive messages
 *  under one day divider. Local timezone (not UTC) so divider matches
 *  the user's perceived day. */
export function dayKey(ts: number): string {
  const d = new Date(ts);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function timeShort(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  const min = Math.floor(diff / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Horizontal day divider — centered uppercase label between hairline
 *  rules. Renders as a semantic separator so screen readers announce
 *  "Tuesday, May 21" without the visual chrome. */
export function DayDivider({ ts }: { ts: number }) {
  const label = dayDividerLabel(ts);
  const abs = new Date(ts).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  return (
    <div
      role="separator"
      aria-label={abs}
      className="flex items-center gap-3 py-1 select-none"
    >
      <div className="h-px flex-1 bg-border/40" aria-hidden />
      <span
        className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70"
        title={abs}
      >
        {label}
      </span>
      <div className="h-px flex-1 bg-border/40" aria-hidden />
    </div>
  );
}
